import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserLayout } from '../components/User/UserLayout';
import {
  Calendar,
  TrendingUp,
  Users,
  MousePointerClick,
  Eye,
  Clock,
  Download,
  Loader,
  ChevronDown,
  Shield,
  Bot,
  ShieldAlert,
  ShieldCheck,
  Trophy,
  Smartphone,
  Monitor,
  Tablet,
  ArrowRightLeft,
  Globe,
  Cpu,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePlanUsage } from '../hooks/usePlanUsage';
import type { PlanTier, PlanUsageState } from '../hooks/usePlanUsage';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface Page {
  id: string;
  name: string;
  views: number;
  uniqueVisitors?: number;
  uniqueClicks?: number;
  conversions?: number;
  slug?: string;
  is_published?: boolean;
}

interface BotData {
  totalBlocked: number;
  byType: Array<{ name: string; value: number; color: string }>;
  byName: Array<{ name: string; count: number }>;
  dailyBlocked: Array<{ date: string; blocked: number }>;
}

interface RedirectData {
  totalRedirects: number;
  byDevice: Array<{ name: string; value: number; color: string }>;
  dailyRedirects: Array<{ date: string; mobile: number; tablet: number; desktop: number; total: number }>;
  redirectUrls: Array<{ url: string; count: number; device: string }>;
}

interface AnalyticsData {
  page: {
    id: string;
    name: string;
    totalViews: number;
  };
  stats: {
    totalViews: number;
    uniqueVisitors: number;
    uniqueClicks: number;
    totalClicks: number;
    totalConversions: number;
    totalRedirects: number;
    avgTimeOnPage: string;
    bounceRate: string;
    conversionRate: string;
  };
  dailyData: Array<{
    date: string;
    views: number;
    uniqueVisitors: number;
    clicks: number;
    conversions: number;
  }>;
  hourlyData?: Array<{
    hour: string;
    views: number;
    uniqueVisitors: number;
    conversions: number;
  }>;
  deviceData: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  osData?: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  browserData?: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  deviceModelData?: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  countryData: Array<{
    country: string;
    visits: number;
  }>;
  cityData?: Array<{
    city: string;
    country: string;
    visits: number;
  }>;
  botData?: BotData;
  redirectData?: RedirectData;
}

type TimeRange = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';

// Country → flag emoji. Hoisted out of the JSX so the Country and
// City breakdown cards (and any future card grouped by country) stay
// in sync without duplicating the table — adding a new country once
// here is enough.
const FLAG_EMOJIS: Record<string, string> = {
  'Thailand': '🇹🇭', 'United States': '🇺🇸', 'United Kingdom': '🇬🇧',
  'Japan': '🇯🇵', 'China': '🇨🇳', 'South Korea': '🇰🇷', 'Singapore': '🇸🇬',
  'Malaysia': '🇲🇾', 'Vietnam': '🇻🇳', 'Indonesia': '🇮🇩', 'Philippines': '🇵🇭',
  'Australia': '🇦🇺', 'India': '🇮🇳', 'Germany': '🇩🇪', 'France': '🇫🇷',
  'Netherlands': '🇳🇱', 'Canada': '🇨🇦', 'Brazil': '🇧🇷', 'Russia': '🇷🇺',
  'Spain': '🇪🇸', 'Italy': '🇮🇹', 'Mexico': '🇲🇽', 'Hong Kong': '🇭🇰',
  'Taiwan': '🇹🇼', 'Laos': '🇱🇦', 'Myanmar': '🇲🇲', 'Cambodia': '🇰🇭',
  'Sweden': '🇸🇪', 'Norway': '🇳🇴', 'Denmark': '🇩🇰', 'Finland': '🇫🇮',
  'Switzerland': '🇨🇭', 'Austria': '🇦🇹', 'Belgium': '🇧🇪', 'Ireland': '🇮🇪',
  'Poland': '🇵🇱', 'Turkey': '🇹🇷', 'Israel': '🇮🇱', 'United Arab Emirates': '🇦🇪',
  'Saudi Arabia': '🇸🇦', 'Egypt': '🇪🇬', 'South Africa': '🇿🇦', 'Argentina': '🇦🇷',
  'Chile': '🇨🇱', 'Colombia': '🇨🇴', 'New Zealand': '🇳🇿', 'Pakistan': '🇵🇰',
  'Bangladesh': '🇧🇩', 'Sri Lanka': '🇱🇰', 'Nepal': '🇳🇵', 'Mongolia': '🇲🇳',
};

const flagFor = (country: string | undefined): string =>
  (country && FLAG_EMOJIS[country]) || '🌍';

export const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = useAuthStore((state) => state.token);
  const { t } = useTranslation('analytics');
  const planUsage = usePlanUsage();

  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  // Tracks the wall-clock time of the last successful fetch so the UI
  // can show "Updated 5s ago" next to the time-range selector.
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Re-render every 10s so the "X sec ago" label stays fresh without
  // having to re-fetch.
  const [tickNow, setTickNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTickNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);
  const [topPageSort, setTopPageSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'uniqueClicks', dir: 'desc' });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch pages list
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await fetch(`${API_URL}/pages`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch pages');
        }

        const data = await response.json();
        setPages(data);

        // Auto-select from URL or default to 'all'
        const pageIdFromUrl = searchParams.get('page');
        if (pageIdFromUrl && data.find((p: Page) => p.id === pageIdFromUrl)) {
          setSelectedPageId(pageIdFromUrl);
        } else {
          setSelectedPageId('all');
        }
      } catch (error) {
        console.error('Fetch pages error:', error);
        toast.error(t('page.loadPagesFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPages();
  }, [token, searchParams, t]);

  // Fetch analytics data
  // Two-tier behavior:
  //   1. Initial / filter-change fetch  → shows the loading spinner.
  //   2. Live polling (today/yesterday) → silent re-fetch every 30s,
  //      only while the tab is visible (skips when user is on another tab
  //      to save battery / API quota). Charts swap data in place without
  //      flashing the skeleton.
  // Also passes the browser's IANA tz (e.g. "Asia/Bangkok") so the server
  // buckets hours/days in the user's calendar — without this, a 14:00
  // Bangkok event lands in the "07:00" UTC bucket.
  useEffect(() => {
    if (!selectedPageId) return;
    if (timeRange === 'custom' && (!customStartDate || !customEndDate)) return;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Bangkok';
    let cancelled = false;

    const fetchAnalytics = async (silent = false) => {
      if (!silent) setIsLoadingAnalytics(true);
      try {
        let url: string;
        if (selectedPageId === 'all') {
          url = `${API_URL}/pages/analytics/all?timeRange=${timeRange}`;
        } else {
          url = `${API_URL}/pages/${selectedPageId}/analytics?timeRange=${timeRange}`;
        }
        if (timeRange === 'custom' && customStartDate && customEndDate) {
          url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
        }
        url += `&tz=${encodeURIComponent(tz)}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch analytics');
        }

        const data = await response.json();
        if (cancelled) return;
        setAnalyticsData(data);
        setLastUpdated(new Date());
      } catch (error) {
        if (cancelled) return;
        console.error('Fetch analytics error:', error);
        if (!silent) toast.error(t('page.loadAnalyticsFailed'));
      } finally {
        if (!cancelled && !silent) setIsLoadingAnalytics(false);
      }
    };

    fetchAnalytics(false);

    // Live polling — only meaningful when the visible window can still
    // change (today/yesterday). For 7d/30d/90d/custom the data is mostly
    // static, so polling just burns API credits.
    let pollId: ReturnType<typeof setInterval> | null = null;
    if (timeRange === 'today' || timeRange === 'yesterday') {
      pollId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchAnalytics(true);
        }
      }, 30_000);
    }

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
    };
  }, [selectedPageId, timeRange, customStartDate, customEndDate, token, t]);

  const handleTimeRangeChange = (range: TimeRange) => {
    if (range === 'custom') {
      setShowCustomPicker(true);
      // Don't change timeRange yet until dates are selected
      if (!customStartDate || !customEndDate) {
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        setCustomStartDate(weekAgo.toISOString().split('T')[0]);
        setCustomEndDate(today.toISOString().split('T')[0]);
      }
      setTimeRange('custom');
    } else {
      setShowCustomPicker(false);
      setTimeRange(range);
    }
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today': return t('filter.rangeToday');
      case 'yesterday': return t('filter.rangeYesterday');
      case '7d': return t('filter.rangeLast7Days');
      case '30d': return t('filter.rangeLast30Days');
      case '90d': return t('filter.rangeLast90Days');
      case 'custom': return t('filter.rangeCustom', { start: customStartDate, end: customEndDate });
      default: return '';
    }
  };

  const handlePageChange = (pageId: string) => {
    setSelectedPageId(pageId);
    setSearchParams({ page: pageId });
  };

  const downloadCsv = (csvContent: string, filename: string) => {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportSummary = () => {
    try {
      const BASE_DOMAIN = 'hitzlink.com';

      const exportData = [...pages]
        .map(page => {
          const uv = (page as any).uniqueVisitors || 0;
          const uc = (page as any).uniqueClicks || 0;
          const convRate = uv > 0 ? ((uc / uv) * 100).toFixed(1) : '0.0';
          const pageUrl = page.slug ? `https://${page.slug}.${BASE_DOMAIN}` : '';
          return {
            name: page.name,
            linkUrl: pageUrl,
            views: page.views || 0,
            uniqueVisitors: uv,
            uniqueClicks: uc,
            conversionRate: `${convRate}%`,
            published: page.is_published !== false ? 'Yes' : 'No',
          };
        })
        .sort((a, b) => b.uniqueClicks - a.uniqueClicks);

      const headers = ['#', 'Page Name', 'Link URL', 'Views', 'Unique Visitors', 'Unique Clicks', 'Conv. Rate', 'Published'];
      const csvRows = [headers.join(',')];

      exportData.forEach((row, idx) => {
        csvRows.push([
          idx + 1,
          `"${row.name.replace(/"/g, '""')}"`,
          `"${row.linkUrl}"`,
          row.views,
          row.uniqueVisitors,
          row.uniqueClicks,
          row.conversionRate,
          row.published,
        ].join(','));
      });

      const totalViews = exportData.reduce((s, r) => s + r.views, 0);
      const totalUV = exportData.reduce((s, r) => s + r.uniqueVisitors, 0);
      const totalUC = exportData.reduce((s, r) => s + r.uniqueClicks, 0);
      const avgConvRate = totalUV > 0 ? ((totalUC / totalUV) * 100).toFixed(1) : '0.0';
      csvRows.push('');
      csvRows.push(['', '"TOTAL"', '', totalViews, totalUV, totalUC, `${avgConvRate}%`, ''].join(','));

      const dateStr = new Date().toISOString().slice(0, 10);
      downloadCsv(csvRows.join('\n'), `analytics-summary-${dateStr}.csv`);
      toast.success(t('export.summaryDownloaded'));
      setShowExportMenu(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('export.exportFailed'));
    }
  };

  const handleExportDaily = async () => {
    try {
      setIsExporting(true);
      setShowExportMenu(false);
      toast.loading(t('export.dailyGenerating'), { id: 'daily-export' });

      const BASE_DOMAIN = 'hitzlink.com';

      let url = `${API_URL}/pages/analytics/daily-export?timeRange=${timeRange}`;
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch daily data');

      const data = await response.json();
      const { dailyPages, dateRange } = data;

      if (!dailyPages || dailyPages.length === 0) {
        toast.error(t('export.dailyEmpty'), { id: 'daily-export' });
        setIsExporting(false);
        return;
      }

      const headers = ['Date', 'Page Name', 'Link URL', 'Views', 'Unique Visitors', 'Unique Clicks', 'Conv. Rate'];
      const csvRows = [headers.join(',')];

      dailyPages.forEach((row: any) => {
        const dateFormatted = new Date(row.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const pageUrl = row.slug ? `https://${row.slug}.${BASE_DOMAIN}` : '';
        csvRows.push([
          `"${dateFormatted}"`,
          `"${(row.pageName || '').replace(/"/g, '""')}"`,
          `"${pageUrl}"`,
          row.views,
          row.uniqueVisitors,
          row.uniqueClicks,
          `${row.convRate}%`,
        ].join(','));
      });

      // TOTAL row
      const totalViews = dailyPages.reduce((s: number, r: any) => s + r.views, 0);
      const totalUV = dailyPages.reduce((s: number, r: any) => s + r.uniqueVisitors, 0);
      const totalUC = dailyPages.reduce((s: number, r: any) => s + r.uniqueClicks, 0);
      const avgConvRate = totalUV > 0 ? ((totalUC / totalUV) * 100).toFixed(1) : '0.0';
      csvRows.push('');
      csvRows.push(['', '"TOTAL"', '', totalViews, totalUV, totalUC, `${avgConvRate}%`].join(','));

      const dateStr = new Date().toISOString().slice(0, 10);
      const rangeLabel = dateRange.start && dateRange.end ? `${dateRange.start}_to_${dateRange.end}` : dateStr;
      downloadCsv(csvRows.join('\n'), `analytics-daily-${rangeLabel}.csv`);
      toast.success(t('export.dailyDownloaded'), { id: 'daily-export' });
    } catch (error) {
      console.error('Daily export error:', error);
      toast.error(t('export.dailyFailed'), { id: 'daily-export' });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </UserLayout>
    );
  }

  if (pages.length === 0) {
    return (
      <UserLayout>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('page.noPagesTitle')}</h2>
            <p className="text-gray-600 mb-6">{t('page.noPagesHint')}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('page.goToDashboard')}
            </button>
          </div>
        </div>
      </UserLayout>
    );
  }

  const stats = analyticsData?.stats || {
    totalViews: 0,
    uniqueVisitors: 0,
    uniqueClicks: 0,
    totalClicks: 0,
    totalConversions: 0,
    totalRedirects: 0,
    avgTimeOnPage: '0:00',
    bounceRate: '0%',
    conversionRate: '0%',
  };

  const dailyData = analyticsData?.dailyData || [];
  const hourlyData = analyticsData?.hourlyData || [];
  const deviceData = analyticsData?.deviceData || [];
  const osData = analyticsData?.osData || [];
  const browserData = analyticsData?.browserData || [];
  const deviceModelData = analyticsData?.deviceModelData || [];
  const countryData = analyticsData?.countryData || [];
  const cityData = analyticsData?.cityData || [];
  const botData = analyticsData?.botData || { totalBlocked: 0, byType: [], byName: [], dailyBlocked: [] };
  const redirectData = analyticsData?.redirectData || { totalRedirects: 0, byDevice: [], dailyRedirects: [], redirectUrls: [] };

  // Bot logo helper
  const getBotLogo = (name: string): { icon: React.ReactNode; bg: string } => {
    const n = name.toLowerCase();
    if (n.includes('facebook') || n.includes('facebot')) {
      return { bg: '#1877F2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> };
    }
    if (n.includes('google')) {
      return { bg: '#4285F4', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg> };
    }
    if (n.includes('twitter') || n.includes('/x')) {
      return { bg: '#000000', icon: <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> };
    }
    if (n.includes('whatsapp')) {
      return { bg: '#25D366', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> };
    }
    if (n.includes('telegram')) {
      return { bg: '#0088cc', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> };
    }
    if (n.includes('linkedin')) {
      return { bg: '#0A66C2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> };
    }
    if (n.includes('pinterest')) {
      return { bg: '#E60023', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/></svg> };
    }
    if (n.includes('tiktok') || n.includes('bytespider')) {
      return { bg: '#000000', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> };
    }
    if (n.includes('discord')) {
      return { bg: '#5865F2', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg> };
    }
    // Default bot icon  
    return { bg: '#ef4444', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg> };
  };

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('page.title')}</h1>
            <p className="text-gray-600 mt-1">{t('page.subtitle')}</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Page Selector */}
            <div className="relative">
              <select
                value={selectedPageId}
                onChange={(e) => handlePageChange(e.target.value)}
                className="appearance-none px-4 py-2 pr-10 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                <option value="all">{t('filter.allPages', { value: pages.reduce((s, p) => s + (p.views || 0), 0).toLocaleString() })}</option>
                {pages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {t('filter.pageOption', { name: page.name, value: (page.views || 0).toLocaleString() })}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Plan retention pill — surfaces the analytics window the
                user actually has access to so locked time-range buttons
                below don't feel arbitrary. Free is highlighted amber
                because 7d is harshly low for any optimisation work. */}
            <RetentionPill
              usage={planUsage}
              t={t as unknown as (key: string, options?: Record<string, unknown>) => string}
            />

            {/* Time Range — buttons exceeding the plan's retention window
                are visually disabled and route to /upgrade. We never
                MUTATE timeRange on a locked click (would silently change
                what the chart shows below the disabled button). The
                hardcoded mirror of server PLAN_QUOTAS.analyticsRetentionDays
                lives in retentionForPlan() so the gate stays correct
                even if /pages/limits hasn't loaded yet. */}
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
              {(() => {
                // While loading, leave everything enabled so we never
                // flash a disabled state at users who actually have
                // access to the range.
                const retention = planUsage.isLoading
                  ? Number.POSITIVE_INFINITY
                  : retentionForPlan(planUsage.plan);
                const lockedRange = (key: TimeRange): boolean => {
                  if (planUsage.isLoading) return false;
                  if (key === 'today' || key === 'yesterday' || key === '7d') return false;
                  if (key === '30d') return retention < 30;
                  if (key === '90d') return retention < 90;
                  if (key === 'custom') return retention < 30; // free only
                  return false;
                };
                return [
                  { key: 'today' as const, label: t('filter.today') },
                  { key: 'yesterday' as const, label: t('filter.yesterday') },
                  { key: '7d' as const, label: t('filter.last7Days') },
                  { key: '30d' as const, label: t('filter.last30Days') },
                  { key: '90d' as const, label: t('filter.last90Days') },
                  { key: 'custom' as const, label: t('filter.custom') },
                ].map(({ key, label }) => {
                  const isLocked = lockedRange(key);
                  const isActive = timeRange === key;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        if (isLocked) {
                          navigate('/upgrade');
                          return;
                        }
                        handleTimeRangeChange(key as TimeRange);
                      }}
                      disabled={isLocked}
                      title={
                        isLocked
                          ? t('analyticsRetention.lockedTooltip', {
                              days: retention === Number.POSITIVE_INFINITY ? 0 : retention,
                            })
                          : undefined
                      }
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : isLocked
                            ? 'text-gray-400 opacity-50 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {label}
                    </button>
                  );
                });
              })()}
            </div>

            {/* Live / last-updated indicator. Only shows the green "Live"
                pulse for time ranges that auto-poll (today/yesterday) —
                for 7d/30d/90d we just show "Updated Xs ago" so the user
                knows the snapshot age without a misleading "live" badge. */}
            {lastUpdated && (
              <LiveIndicator
                lastUpdated={lastUpdated}
                isLive={timeRange === 'today' || timeRange === 'yesterday'}
                tickNow={tickNow}
                t={t as unknown as (key: string, options?: Record<string, unknown>) => string}
              />
            )}

            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                {isExporting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {t('export.button')}
                <ChevronDown className="w-3 h-3" />
              </button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 py-1 min-w-[220px]">
                    <button
                      onClick={handleExportSummary}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Download className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{t('export.summaryTitle')}</div>
                        <div className="text-xs text-gray-500">{t('export.summaryDesc')}</div>
                      </div>
                    </button>
                    <button
                      onClick={handleExportDaily}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{t('export.dailyTitle')}</div>
                        <div className="text-xs text-gray-500">{t('export.dailyDesc')}</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Custom Date Picker */}
        {showCustomPicker && timeRange === 'custom' && (
          <div className="bg-white border border-blue-200 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
            <Calendar className="w-5 h-5 text-blue-500" />
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">{t('filter.from')}</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">{t('filter.to')}</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {isLoadingAnalytics ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">{t('stats.totalViews')}</p>
                  <Eye className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.totalViews.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {getTimeRangeLabel()}
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-indigo-700 font-medium">{t('stats.uniqueVisitors')}</p>
                  <Users className="w-5 h-5 text-indigo-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{(stats.uniqueVisitors || 0).toLocaleString()}</p>
                <p className="text-xs text-indigo-500 mt-2">
                  {stats.totalViews > 0
                    ? t('stats.uniqueVisitorsRatio', { pct: ((stats.uniqueVisitors / stats.totalViews) * 100).toFixed(0) })
                    : t('stats.noViewsYet')}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-purple-700 font-medium">{t('stats.uniqueClicks')}</p>
                  <MousePointerClick className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{(stats.uniqueClicks || 0).toLocaleString()}</p>
                <p className="text-xs text-purple-500 mt-2">
                  {t('stats.totalClicksHint', { count: stats.totalClicks })}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">{t('stats.conversionRate')}</p>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.conversionRate}</p>
                <p className="text-xs text-gray-600 mt-2">
                  {t('stats.conversionFraction', { clicks: (stats.uniqueClicks || 0), visitors: (stats.uniqueVisitors || 0) })}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">{t('stats.avgTimeOnPage')}</p>
                  <Clock className="w-5 h-5 text-orange-500" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.avgTimeOnPage}</p>
                <p className="text-xs text-gray-600 mt-2">
                  {t('stats.bounceRate', { value: stats.bounceRate })}
                </p>
              </div>
            </div>

            {/* Top Performance - My Landing Pages */}
            {pages.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    {t('topPerformance.heading')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('topPerformance.sortedBy', {
                      column: topPageSort.key === 'uniqueClicks'
                        ? t('topPerformance.columnUniqueClicks')
                        : topPageSort.key === 'views'
                          ? t('topPerformance.columnViews')
                          : topPageSort.key === 'uniqueVisitors'
                            ? t('topPerformance.columnUniqueVisitors')
                            : t('topPerformance.columnConvRate'),
                    })}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('topPerformance.columnPageName')}</th>
                        {[
                          { key: 'views', label: t('topPerformance.columnViews') },
                          { key: 'uniqueVisitors', label: t('topPerformance.columnUniqueVisitors') },
                          { key: 'uniqueClicks', label: t('topPerformance.columnUniqueClicks') },
                          { key: 'convRate', label: t('topPerformance.columnConvRate') },
                        ].map(col => (
                          <th
                            key={col.key}
                            className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                            style={{ color: topPageSort.key === col.key ? '#2563eb' : '#374151' }}
                            onClick={() => setTopPageSort(prev => ({
                              key: col.key,
                              dir: prev.key === col.key ? (prev.dir === 'desc' ? 'asc' : 'desc') : 'desc',
                            }))}
                          >
                            <span className="inline-flex items-center gap-1 justify-end">
                              {col.label}
                              {topPageSort.key === col.key && (
                                <span className="text-blue-600">{topPageSort.dir === 'desc' ? '↓' : '↑'}</span>
                              )}
                            </span>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('topPerformance.columnPerformance')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...pages]
                        .map(page => {
                          const uv = (page as any).uniqueVisitors || 0;
                          const uc = (page as any).uniqueClicks || 0;
                          const convRate = uv > 0 ? ((uc / uv) * 100) : 0;
                          return { ...page, _uv: uv, _uc: uc, _convRate: convRate };
                        })
                        .sort((a, b) => {
                          const keyMap: Record<string, string> = { views: 'views', uniqueVisitors: '_uv', uniqueClicks: '_uc', convRate: '_convRate' };
                          const k = keyMap[topPageSort.key] || '_uc';
                          const aVal = (a as any)[k] || 0;
                          const bVal = (b as any)[k] || 0;
                          return topPageSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
                        })
                        .slice(0, 10)
                        .map((page, index) => {
                          const keyMap: Record<string, string> = { views: 'views', uniqueVisitors: '_uv', uniqueClicks: '_uc', convRate: '_uc' };
                          const sortValKey = keyMap[topPageSort.key] || '_uc';
                          const maxVal = Math.max(...pages.map(p => {
                            if (sortValKey === '_uv') return (p as any).uniqueVisitors || 0;
                            if (sortValKey === '_uc') return (p as any).uniqueClicks || 0;
                            return p.views || 0;
                          }), 1);
                          const curVal = (page as any)[sortValKey] || 0;
                          
                          return (
                            <tr 
                              key={page.id} 
                              className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                                page.id === selectedPageId ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => handlePageChange(page.id)}
                            >
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                                  index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  index === 1 ? 'bg-gray-100 text-gray-600' :
                                  index === 2 ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-50 text-gray-500'
                                }`}>
                                  {index + 1}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-medium text-gray-900">{page.name}</div>
                                  {page.is_published === false && (
                                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{t('topPerformance.draftBadge')}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <span className="text-sm font-semibold text-gray-900">{(page.views || 0).toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <span className="text-sm text-indigo-600 font-medium">{page._uv.toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <span className="text-sm text-purple-600 font-medium">{page._uc.toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <span className={`text-sm font-bold ${
                                  page._convRate >= 5 ? 'text-green-600' : 
                                  page._convRate >= 2 ? 'text-yellow-600' : 'text-gray-500'
                                }`}>{page._convRate.toFixed(1)}%</span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="w-full max-w-[120px] bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                                    style={{ width: `${Math.min((curVal / maxVal) * 100, 100)}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Charts */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {/* Views Trend
                  ─────────────────────────────────────────────────────
                  Two render paths so a single-day window doesn't show
                  three lonely dots that look like a broken chart:

                  - 1 data point → big stat tiles with delta-friendly
                    numbers and a derived conversion rate. Far more
                    scannable than a 1-point line.
                  - 2+ data points → stacked-style Area chart with
                    gradient fills + visible point markers, so trends
                    read at a glance even on small ranges. */}
              <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">{t('trend.title')}</h3>
                {dailyData.length === 0 && (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">
                    {t('trend.noData')}
                  </div>
                )}

                {dailyData.length === 1 && (() => {
                  const d = dailyData[0];
                  const convRate = d.views > 0 ? (d.conversions / d.views) * 100 : 0;
                  const tiles: Array<{
                    label: string;
                    value: number;
                    suffix?: string;
                    color: string;
                    bg: string;
                    border: string;
                  }> = [
                    {
                      label: t('trend.tileViews'),
                      value: d.views,
                      color: 'text-blue-600',
                      bg: 'bg-blue-50',
                      border: 'border-blue-200',
                    },
                    {
                      label: t('trend.tileUniqueVisitors'),
                      value: d.uniqueVisitors,
                      color: 'text-purple-600',
                      bg: 'bg-purple-50',
                      border: 'border-purple-200',
                    },
                    {
                      label: t('trend.tileConversions'),
                      value: d.conversions,
                      color: 'text-emerald-600',
                      bg: 'bg-emerald-50',
                      border: 'border-emerald-200',
                    },
                  ];
                  return (
                    <div className="space-y-4">
                      <div className="text-xs text-gray-500">{t('trend.singleDayHeader', { date: d.date })}</div>
                      <div className="grid grid-cols-3 gap-3">
                        {tiles.map(tile => (
                          <div
                            key={tile.label}
                            className={`rounded-lg border ${tile.border} ${tile.bg} p-4`}
                          >
                            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                              {tile.label}
                            </div>
                            <div className={`text-3xl font-bold mt-1 ${tile.color}`}>
                              {tile.value.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-baseline justify-between">
                        <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                          {t('trend.convRateLabel')}
                        </span>
                        <span className="text-2xl font-bold text-gray-900">
                          {convRate.toFixed(2)}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('trend.tip')}
                      </p>
                    </div>
                  );
                })()}

                {dailyData.length >= 2 && (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dailyData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="gConversions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.55} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#6b7280"
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                      />
                      {/* Two Y axes so the small Conversions series
                          isn't crushed by the much larger Views axis. */}
                      <YAxis
                        yAxisId="left"
                        stroke="#6b7280"
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                        width={40}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="#10b981"
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                        width={32}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid #e5e7eb',
                          fontSize: 12,
                        }}
                        formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="views"
                        name={t('trend.legendViews')}
                        stroke="#3b82f6"
                        strokeWidth={2}
                        fill="url(#gViews)"
                        dot={{ r: 3, strokeWidth: 0, fill: '#3b82f6' }}
                        activeDot={{ r: 5 }}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="uniqueVisitors"
                        name={t('trend.legendUniqueVisitors')}
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        fill="url(#gVisitors)"
                        dot={{ r: 3, strokeWidth: 0, fill: '#8b5cf6' }}
                        activeDot={{ r: 5 }}
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="conversions"
                        name={t('trend.legendConversions')}
                        stroke="#10b981"
                        strokeWidth={2}
                        fill="url(#gConversions)"
                        dot={{ r: 3, strokeWidth: 0, fill: '#10b981' }}
                        activeDot={{ r: 5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Device Distribution */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">{t('device.title')}</h3>
                {deviceData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={deviceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {deviceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {deviceData.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-sm text-gray-600">{item.name}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-gray-400">
                    {t('device.noData')}
                  </div>
                )}
              </div>
            </div>

            {/* Hourly Traffic Distribution */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-5 h-5 text-orange-500" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('hourly.title')}</h3>
                  <p className="text-sm text-gray-500">{t('hourly.subtitle', { range: getTimeRangeLabel() })}</p>
                </div>
              </div>
              {hourlyData.length > 0 && hourlyData.some(h => h.views > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={hourlyData}>
                    <defs>
                      <linearGradient id="hourlyViewsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="hourlyUVGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="hour"
                      stroke="#6b7280"
                      tick={{ fontSize: 11 }}
                      interval={1}
                    />
                    <YAxis stroke="#6b7280" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      labelFormatter={(label) => `${label} - ${String(Number(label.split(':')[0]) + 1).padStart(2, '0')}:00`}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="views"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fill="url(#hourlyViewsGradient)"
                      name={t('trend.legendViews')}
                    />
                    <Area
                      type="monotone"
                      dataKey="uniqueVisitors"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#hourlyUVGradient)"
                      name={t('trend.legendUniqueVisitors')}
                      strokeDasharray="5 5"
                    />
                    <Area
                      type="monotone"
                      dataKey="conversions"
                      stroke="#10b981"
                      strokeWidth={2}
                      fill="none"
                      name={t('trend.legendConversions')}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex flex-col items-center justify-center text-gray-400">
                  <Clock className="w-10 h-10 mb-3 text-gray-300" />
                  <p>{t('hourly.noData')}</p>
                </div>
              )}
              {hourlyData.length > 0 && hourlyData.some(h => h.views > 0) && (() => {
                const peakHour = hourlyData.reduce((max, h) => h.views > max.views ? h : max, hourlyData[0]);
                const quietHour = hourlyData.reduce((min, h) => h.views < min.views ? h : min, hourlyData[0]);
                const totalViews = hourlyData.reduce((sum, h) => sum + h.views, 0);
                return (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-600 font-medium">{t('hourly.peakHour')}</p>
                      <p className="text-lg font-bold text-gray-900">{peakHour.hour}</p>
                      <p className="text-xs text-gray-500">{t('hourly.viewsCount', { count: peakHour.views })}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 font-medium">{t('hourly.quietHour')}</p>
                      <p className="text-lg font-bold text-gray-900">{quietHour.hour}</p>
                      <p className="text-xs text-gray-500">{t('hourly.viewsCount', { count: quietHour.views })}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-purple-600 font-medium">{t('hourly.avgPerHour')}</p>
                      <p className="text-lg font-bold text-gray-900">{(totalViews / 24).toFixed(1)}</p>
                      <p className="text-xs text-gray-500">{t('hourly.viewsPerHour')}</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Detailed Device Breakdown */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Operating Systems */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Cpu className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">{t('os.title')}</h3>
                </div>
                {osData.length > 0 ? (
                  <div className="space-y-3">
                    {osData.map((item, index) => (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.name}</span>
                          <span className="text-xs text-gray-500">{item.count.toLocaleString()} ({item.percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[150px] text-gray-400 text-sm">
                    {t('os.noData')}
                  </div>
                )}
              </div>

              {/* Browsers */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">{t('browsers.title')}</h3>
                </div>
                {browserData.length > 0 ? (
                  <div className="space-y-3">
                    {browserData.map((item, index) => {
                      const colors = ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#7c3aed', '#6d28d9', '#5b21b6'];
                      return (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{item.name}</span>
                            <span className="text-xs text-gray-500">{item.count.toLocaleString()} ({item.percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${item.percentage}%`, backgroundColor: colors[index % colors.length] }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[150px] text-gray-400 text-sm">
                    {t('browsers.noData')}
                  </div>
                )}
              </div>

              {/* Device Models */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Smartphone className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-gray-900">{t('deviceModels.title')}</h3>
                </div>
                {deviceModelData.length > 0 ? (
                  <div className="space-y-3">
                    {deviceModelData.map((item, index) => {
                      const colors = ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#059669', '#047857', '#065f46'];
                      return (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]" title={item.name}>{item.name}</span>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{item.count.toLocaleString()} ({item.percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${item.percentage}%`, backgroundColor: colors[index % colors.length] }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[150px] text-gray-400 text-sm">
                    {t('deviceModels.noData')}
                  </div>
                )}
              </div>
            </div>

            {/* Click Activity + Top Countries */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Clicks Trend */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">{t('clicks.title')}</h3>
                {dailyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Bar dataKey="clicks" fill="#8b5cf6" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    {t('clicks.noData')}
                  </div>
                )}
              </div>

              {/* Top Countries */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Globe className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-bold text-gray-900">{t('countries.title')}</h3>
                </div>
                {countryData.length > 0 ? (
                  <div className="space-y-3">
                    {(() => {
                      const totalVisits = countryData.reduce((sum, c) => sum + c.visits, 0);
                      return countryData.map((item, index) => {
                        const pct = totalVisits > 0 ? Math.round((item.visits / totalVisits) * 100) : 0;
                        return (
                          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-center w-8 h-8 text-lg">
                              {flagFor(item.country)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-gray-900 text-sm truncate">{item.country}</span>
                                <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">{item.visits.toLocaleString()} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div className="bg-gradient-to-r from-orange-400 to-orange-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-gray-400">
                    {t('countries.noData')}
                  </div>
                )}
              </div>
            </div>

            {/* Top Cities — own row so the city name + bar have room
                to breathe on wide screens (matches the Plausible/Fathom
                style the user referenced). */}
            <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Globe className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-bold text-gray-900">{t('cities.title')}</h3>
              </div>
              {cityData.length > 0 ? (
                <div className="space-y-3">
                  {(() => {
                    const totalVisits = cityData.reduce((sum, c) => sum + c.visits, 0);
                    return cityData.map((item, index) => {
                      const pct = totalVisits > 0 ? Math.round((item.visits / totalVisits) * 100) : 0;
                      return (
                        <div key={`${item.city}-${item.country}-${index}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-center w-8 h-8 text-lg">
                            {flagFor(item.country)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-gray-900 text-sm truncate">{item.city}</span>
                              <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">{item.visits.toLocaleString()} ({pct}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div className="bg-gradient-to-r from-orange-400 to-orange-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-gray-400">
                  {t('cities.noData')}
                </div>
              )}
            </div>

            {/* =============================================
                Device Redirect Analytics Section
                ============================================= */}
            {redirectData.totalRedirects > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
                    <ArrowRightLeft className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('redirect.sectionTitle')}</h2>
                    <p className="text-sm text-gray-500">{t('redirect.sectionSubtitle', { range: getTimeRangeLabel() })}</p>
                  </div>
                </div>

                {/* Redirect Stats Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-blue-700">{t('redirect.totalRedirects')}</p>
                      <ArrowRightLeft className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{redirectData.totalRedirects.toLocaleString()}</p>
                    <p className="text-xs text-blue-600 mt-1">{getTimeRangeLabel()}</p>
                  </div>

                  {/* Per-device cards */}
                  {[
                    { key: 'Mobile', label: t('redirect.mobile'), icon: <Smartphone className="w-5 h-5 text-blue-500" />, color: 'blue' },
                    { key: 'Tablet', label: t('redirect.tablet'), icon: <Tablet className="w-5 h-5 text-emerald-500" />, color: 'emerald' },
                    { key: 'Desktop', label: t('redirect.desktop'), icon: <Monitor className="w-5 h-5 text-purple-500" />, color: 'purple' },
                  ].map(({ key, label, icon }) => {
                    const count = redirectData.byDevice.find(d => d.name === key)?.value || 0;
                    const pct = redirectData.totalRedirects > 0
                      ? ((count / redirectData.totalRedirects) * 100).toFixed(1)
                      : '0.0';
                    return (
                      <div key={key} className={`bg-white border border-gray-200 rounded-xl p-5`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm text-gray-600">{label}</p>
                          {icon}
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{count.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('redirect.deviceShare', { pct })}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mb-6">
                  {/* Redirect by Device Pie Chart */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t('redirect.byDeviceTitle')}</h3>
                    {redirectData.byDevice.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={redirectData.byDevice}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {redirectData.byDevice.map((entry, index) => (
                                <Cell key={`redirect-cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="mt-4 space-y-2">
                          {redirectData.byDevice.map((item, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                <span className="text-sm text-gray-600">{item.name}</span>
                              </div>
                              <span className="text-sm font-bold text-gray-900">{item.value.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-gray-400">
                        {t('redirect.byDeviceNoData')}
                      </div>
                    )}
                  </div>

                  {/* Redirect URLs */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t('redirect.destinationsTitle')}</h3>
                    {redirectData.redirectUrls.length > 0 ? (
                      <div className="space-y-3">
                        {redirectData.redirectUrls.map((item, index) => {
                          const maxCount = redirectData.redirectUrls[0]?.count || 1;
                          const percentage = (item.count / maxCount) * 100;
                          return (
                            <div key={index} className="relative">
                              <div className="flex items-center justify-between relative z-10 p-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                                    {item.device === 'mobile' ? <Smartphone className="w-3.5 h-3.5" /> : 
                                     item.device === 'tablet' ? <Tablet className="w-3.5 h-3.5" /> : 
                                     <Monitor className="w-3.5 h-3.5" />}
                                  </div>
                                  <span className="font-medium text-gray-900 text-sm truncate">{item.url}</span>
                                </div>
                                <span className="font-bold text-gray-900 text-sm ml-3 flex-shrink-0">{item.count.toLocaleString()}</span>
                              </div>
                              <div className="absolute inset-0 bg-blue-50 rounded-lg" style={{ width: `${percentage}%` }} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-gray-400">
                        {t('redirect.destinationsNoData')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Daily Redirect Trend */}
                {redirectData.dailyRedirects.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">{t('redirect.dailyTitle')}</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={redirectData.dailyRedirects}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="mobile" stackId="a" fill="#3b82f6" name={t('redirect.mobile')} radius={[0, 0, 0, 0]} />
                        <Bar dataKey="tablet" stackId="a" fill="#10b981" name={t('redirect.tablet')} />
                        <Bar dataKey="desktop" stackId="a" fill="#8b5cf6" name={t('redirect.desktop')} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* =============================================
                Bot Cloaking Analytics Section
                ============================================= */}
            <div className="mt-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{t('bot.sectionTitle')}</h2>
                  <p className="text-sm text-gray-500">{t('bot.sectionSubtitle')}</p>
                </div>
              </div>

              {/* Bot Stats Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-red-700">{t('bot.blockedTotal')}</p>
                    <ShieldAlert className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{botData.totalBlocked.toLocaleString()}</p>
                  <p className="text-xs text-red-600 mt-2">
                    {getTimeRangeLabel()}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-700">{t('bot.typesDetected')}</p>
                    <Bot className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{botData.byType.length}</p>
                  <p className="text-xs text-blue-600 mt-2">
                    {t('bot.typesUnit')}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-700">{t('bot.protectionRate')}</p>
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats.totalViews + botData.totalBlocked > 0
                      ? `${((botData.totalBlocked / (stats.totalViews + botData.totalBlocked)) * 100).toFixed(1)}%`
                      : '0%'}
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    {t('bot.protectionRateHint')}
                  </p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Bot by Channel / Type */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">{t('bot.byChannelTitle')}</h3>
                  {botData.byType.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={botData.byType}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {botData.byType.map((entry, index) => (
                              <Cell key={`bot-cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-4 space-y-2">
                        {botData.byType.map((item, index) => {
                          const channelLogo = getBotLogo(item.name);
                          return (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center justify-center w-5 h-5 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: channelLogo.bg }}>
                                  {channelLogo.icon}
                                </div>
                                <span className="text-sm text-gray-600">{item.name}</span>
                              </div>
                              <span className="text-sm font-bold text-gray-900">{item.value}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                      <ShieldCheck className="w-12 h-12 mb-3 text-gray-300" />
                      <p>{t('bot.byChannelNoData')}</p>
                      <p className="text-xs mt-1">{t('bot.byChannelHint')}</p>
                    </div>
                  )}
                </div>

                {/* Top Blocked Bots */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">{t('bot.topBotsTitle')}</h3>
                  {botData.byName.length > 0 ? (
                    <div className="space-y-3">
                      {botData.byName.map((item, index) => {
                        const maxCount = botData.byName[0]?.count || 1;
                        const percentage = (item.count / maxCount) * 100;
                        const botLogo = getBotLogo(item.name);
                        return (
                          <div key={index} className="relative">
                            <div className="flex items-center justify-between relative z-10 p-3">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-7 h-7 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: botLogo.bg }}>
                                  {botLogo.icon}
                                </div>
                                <span className="font-medium text-gray-900 text-sm">{item.name}</span>
                              </div>
                              <span className="font-bold text-gray-900 text-sm">{item.count.toLocaleString()}</span>
                            </div>
                            <div
                              className="absolute inset-0 bg-red-50 rounded-lg"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                      <Bot className="w-12 h-12 mb-3 text-gray-300" />
                      <p>{t('bot.topBotsNoData')}</p>
                      <p className="text-xs mt-1">{t('bot.topBotsHint')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Daily Blocked Trend */}
              {botData.dailyBlocked.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">{t('bot.dailyTitle')}</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={botData.dailyBlocked}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Bar dataKey="blocked" fill="#ef4444" name={t('bot.dailyLegend')} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </UserLayout>
  );
};

/**
 * Small badge that shows either:
 *   • Green pulsing "Live · 5s ago"  (when the parent is auto-polling)
 *   • Neutral "Updated 2m ago"        (for static date ranges)
 *
 * `tickNow` is the parent's clock-tick prop that re-renders every 10s
 * — without it, the "X ago" string would be frozen at first render.
 */
const LiveIndicator: React.FC<{
  lastUpdated: Date;
  isLive: boolean;
  tickNow: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ lastUpdated, isLive, tickNow, t }) => {
  // Touch tickNow so React knows this component depends on it.
  void tickNow;
  const seconds = Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000));
  const ago =
    seconds < 5 ? t('live.justNow')
    : seconds < 60 ? t('live.secondsAgo', { n: seconds })
    : seconds < 3600 ? t('live.minutesAgo', { n: Math.floor(seconds / 60) })
    : t('live.hoursAgo', { n: Math.floor(seconds / 3600) });

  if (isLive) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
        </span>
        <span className="font-semibold text-emerald-700">{t('live.label')}</span>
        <span className="text-emerald-600">{t('live.withAgo', { ago })}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      {t('live.updated', { ago })}
    </div>
  );
};

/**
 * Mirror of server PLAN_QUOTAS.analyticsRetentionDays. Phase 7 keeps
 * this hardcoded so the time-range gate stays correct on first paint
 * (before /pages/limits returns) — the hook then overrides via
 * planUsage.retentionDays once the server replies. Keep in lockstep
 * with server/middleware/planLimits.js PLAN_QUOTAS.
 */
function retentionForPlan(plan: PlanTier | null): number {
  switch (plan) {
    case 'pro':
      return 90;
    case 'agency':
      return 365;
    case 'enterprise':
      return 730;
    case 'free':
    case null:
    default:
      return 7;
  }
}

const RetentionPill: React.FC<{
  usage: PlanUsageState;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ usage, t }) => {
  if (usage.isLoading || usage.plan === null) {
    return <div className="h-9 w-44 animate-pulse rounded-lg bg-gray-100" />;
  }

  const days = usage.retentionDays ?? retentionForPlan(usage.plan);
  const isFreeHarsh = usage.plan === 'free';
  const containerClass = isFreeHarsh
    ? 'flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800'
    : 'flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600';

  return (
    <div className={containerClass} title={t('analyticsRetention.hint', { plan: usage.plan, days })}>
      <Clock className={`w-3.5 h-3.5 ${isFreeHarsh ? 'text-amber-700' : 'text-gray-500'}`} />
      <span className="font-medium">{t('analyticsRetention.label', { days })}</span>
    </div>
  );
};
